import { useState } from 'react';
import { Button, Drawer, Input, message } from 'antd';
import { ShoppingBag, Minus, Plus, X } from 'lucide-react';
import { useTranslation } from "react-i18next";
import { useCart } from "../../context/UserCartContext.jsx";
import useFetchProducts from "../../hooks/useFetchProducts";
import { useNavigate } from 'react-router-dom';
import ContactUs from "../contact/ContactUs.jsx";
import { CloseOutlined } from "@ant-design/icons";
import useCreateOrder from "../../hooks/useCreateOrder"; // Импортируем хук

const UserCart = () => {
    const [drawerVisible, setDrawerVisible] = useState(false);
    const [isCheckout, setIsCheckout] = useState(false);
    const [customerName, setCustomerName] = useState("");
    const [phoneNumber, setPhoneNumber] = useState("");
    const [phoneError, setPhoneError] = useState(false);
    const [phoneTouched, setPhoneTouched] = useState(false);

    const { cart, removeFromCart, updateCartQty, clearCart } = useCart();
    const { products } = useFetchProducts();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { placeOrder, loading, setLoading, error } = useCreateOrder();
    const [messageApi, contextHolder] = message.useMessage();

    const toggleDrawer = () => {
        setDrawerVisible(!drawerVisible);
        setIsCheckout(false);
        setCustomerName("");
        setPhoneNumber("");
        setPhoneError(false);
        setPhoneTouched(false);
    };

    const getProductById = (id) => products.find(product => product.id === id) || null;

    const handleIncrease = (item) => {
        const product = getProductById(item.id);
        if (product && item.qty < product.qty) {
            updateCartQty(item.id, item.qty + 1, product.qty);
        }
    };

    const handleDecrease = (item) => {
        const product = getProductById(item.id);
        if (product && item.qty > 1) {
            updateCartQty(item.id, item.qty - 1, product.qty);
        }
    };

    const getTotalPrice = () => cart.reduce((total, item) => {
        const product = getProductById(item.id);
        return total + (product ? product.price * item.qty : 0);
    }, 0);

    const validatePhoneNumber = (value) => {
        return /^9\d{9}$/.test(value);
    };

    const handlePhoneChange = (e) => {
        const value = e.target.value.replace(/\D/g, "");
        setPhoneNumber(value);
        setPhoneError(!validatePhoneNumber(value));
    };

    const handlePhoneBlur = () => {
        setPhoneTouched(true);
    };

    const handleCheckout = async () => {
        setLoading(true);

        const validCustomerName = typeof customerName === "string" ? customerName.trim() : "";

        if (!validCustomerName || phoneError || !phoneNumber) {
            setLoading(false);
            messageApi.warning(t("unfilledNecessaryFieldsError"));
            return;
        }

        const productsList = cart.map((item) => {
            const product = getProductById(item.id);
            return JSON.stringify({
                qty: item.qty,
                artikul: String(product?.artikul || "unknown"),
            });
        });

        const productsStr = productsList.join(";");

        const order = {
            phone: phoneNumber,
            name: validCustomerName,
            products: productsStr,
        };

        try {
            await placeOrder(order, () => {
                clearCart();
                localStorage.removeItem("cart");
                setDrawerVisible(false);
                navigate(`/purchased`);
            });
        } catch (error) {
            console.error("Ошибка оформления заказа:", error);
            messageApi.error(t("orderFailedError"));
        } finally {
            setLoading(false);
        }
    };

    if (error) {
        messageApi.error(error.message);
    }

    return (
        <div>
            {contextHolder}
            <Button
                type="link"
                size="middle"
                icon={<ShoppingBag color="black" size={32} strokeWidth={1} />}
                onClick={toggleDrawer}
            />
            <Drawer
                placement="right"
                onClose={toggleDrawer}
                open={drawerVisible}
                width={382}
            >
                <div className="flex flex-col h-full">
                    <Button
                        type="link"
                        size="large"
                        icon={<CloseOutlined style={{ color: "black", fontSize: "24px" }} />}
                        style={{
                            position: "absolute",
                            top: 82,
                            right: 16,
                        }}
                        onClick={toggleDrawer}
                    />
                    <h2 className="text-3xl font-bold mb-4">
                        {isCheckout ? t("checkout") : cart.length === 0 ? t("emptyCart") : `${t("cart", { count: cart.length })}`}
                    </h2>

                    <div className="flex-1 overflow-y-auto">
                        {!isCheckout ? (
                            cart.map((item) => {
                                const product = getProductById(item.id);
                                const totalItemPrice = product ? product.price * item.qty : 0;
                                return (
                                    <div key={item.id} className="flex justify-between items-center py-2">
                                        <div className="flex items-center gap-4">
                                            <img src={item.image} alt={item.name} className="w-12 h-12 object-contain"/>
                                            <div>
                                                <p>{item.name}</p>
                                                <div className="text-gray-600">
                                                    {item.qty > 1 ? `x${item.qty} / ${totalItemPrice} ₽` : `x1 / ${totalItemPrice} ₽`}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-0">
                                            <Button type="link" onClick={() => handleDecrease(item)}>
                                                <Minus color="black" size={16} />
                                            </Button>
                                            <span className="text-lg font-medium">{item.qty}</span>
                                            <Button type="link" onClick={() => handleIncrease(item)} disabled={product && item.qty >= product.qty}>
                                                <Plus color="black" size={16} />
                                            </Button>
                                            <Button type="link" onClick={() => removeFromCart(item.id)}>
                                                <X color="black" size={20} />
                                            </Button>
                                        </div>
                                    </div>
                                );
                            })
                        ) : (
                            <div className="flex flex-col gap-4">
                                <Input
                                    placeholder={t("enterFCs")}
                                    size="large"
                                    value={customerName}
                                    onChange={(e) => setCustomerName(e.target.value)}
                                />
                                <Input
                                    addonBefore="+7"
                                    maxLength={10}
                                    placeholder={t("enterPhone")}
                                    size="large"
                                    value={phoneNumber}
                                    onChange={handlePhoneChange}
                                    onBlur={handlePhoneBlur}
                                    status={phoneTouched && phoneError ? "error" : ""}
                                />
                                <ContactUs/>
                            </div>
                        )}
                    </div>

                    {cart.length > 0 && (
                        <div className="mt-auto">
                            {!isCheckout ? (
                                <>
                                    <div className="flex justify-between items-center mb-4">
                                        <span className="font-normal text-lg">{t("total")}</span>
                                        <span className="font-medium text-3xl">{getTotalPrice()} ₽</span>
                                    </div>
                                    <div>
                                        <Button
                                            size="large"
                                            variant="solid"
                                            type="primary"
                                            onClick={() => setIsCheckout(true)}
                                            className="w-full"
                                            style={{
                                                backgroundColor: "#0da98c",
                                                borderColor: "#0da98c",
                                                color: "white",
                                                borderRadius: 0,
                                                fontSize: "0.775rem"
                                            }}
                                        >{t("checkout")}
                                        </Button>
                                    </div>
                                </>
                            ) : (
                                <Button
                                    size="large"
                                    type="primary"
                                    block
                                    onClick={handleCheckout}
                                    disabled={loading}
                                    loading={loading}
                                    style={{
                                        backgroundColor: "#0da98c",
                                        borderColor: "#0da98c",
                                        color: "white",
                                        borderRadius: 0,
                                        fontSize: "0.775rem"
                                    }}
                                >{t("confirmOrder")}
                                </Button>
                            )}
                        </div>
                    )}
                </div>
            </Drawer>
        </div>
    );
};

export default UserCart;